import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { PaginationQuery, PaginationState } from "~/modules/filters/types";
import { LoadingSpinner } from "../LoadingSpinner";
import { getWindowedPageData, ELLIPSIS_SPACER } from "./getWindowedPageData";
import { ItemsPerPageSelect } from "./ItemsPerPageSelect";

// // // //

export function PaginationPager(props: {
    loading?: boolean;
    paginationState: PaginationState;
    onChange: (updatedPagination: PaginationQuery) => void;
    className?: string;
    showCount?: boolean;
}) {
    const {
        loading,
        paginationState,
        onChange,
        className,
        showCount = true,
    } = props;
    const { page, perPage, pageCount, resultCount } = paginationState;

    // Define start + end for pagination
    const start = page > 1 ? (page - 1) * perPage : 1;
    let end = (page - 1) * perPage + perPage;
    end = end > resultCount ? resultCount : end;

    return (
        <div
            className={cn(
                "flex flex-1 justify-between items-center text-slate-600",
                className,
            )}
        >
            <button
                onClick={() => {
                    onChange({
                        page: page - 1,
                        perPage,
                    });
                }}
                disabled={page === 1 || loading}
                className={cn(
                    "relative inline-flex items-center rounded-md border bg-card px-4 py-2 text-sm text-slate-600",
                    {
                        "hover:bg-slate-50": page !== 1 && !loading,
                        "cursor-not-allowed opacity-50": page === 1 && !loading,
                        "cursor-wait opacity-50": loading,
                    },
                )}
            >
                Previous
            </button>
            {showCount && (
                <div>
                    <p className="font-light  text-center">
                        Showing <span>{start.toLocaleString()}</span> to{" "}
                        <span>{end.toLocaleString()}</span> of{" "}
                        <span>{resultCount.toLocaleString()}</span> results
                    </p>
                </div>
            )}
            <button
                onClick={() => {
                    onChange({
                        page: page + 1,
                        perPage,
                    });
                }}
                disabled={page === pageCount || loading}
                className={cn(
                    "relative ml-3 inline-flex items-center rounded-md border bg-card px-4 py-2 text-sm text-slate-800",
                    {
                        "hover:bg-slate-50": page !== pageCount && !loading,
                        "cursor-not-allowed opacity-50":
                            page === pageCount && !loading,
                        "cursor-wait opacity-50": loading,
                    },
                )}
            >
                Next
            </button>
        </div>
    );
}

/**
 * Pagination
 * Renders windowed pagination UI
 * ENHANCEMENT - clean up this code a bit more, add more tests
 */
export function Pagination(props: {
    loading?: boolean;
    disableControls: boolean;
    enableItemsPerPageSelect?: boolean;
    paginationState: PaginationState;
    onChange: (updatedPagination: PaginationQuery) => void;
}) {
    const {
        loading = false,
        disableControls,
        enableItemsPerPageSelect = false,
        paginationState,
        onChange,
    } = props;
    const { page, perPage, pageCount, resultCount } = paginationState;

    const shown = getWindowedPageData({
        totalPages: pageCount,
        currentPage: page,
    });

    // Define start + end for pagination
    const start = page > 1 ? (page - 1) * perPage : 1;
    let end = (page - 1) * perPage + perPage;
    end = end > resultCount ? resultCount : end;

    return (
        <div className="flex text-sm items-center justify-between py-2 select-none text-slate-600 w-full">
            {/* Responsive Prev/Next pages */}
            {disableControls !== true && (
                <div className="flex flex-1 justify-between items-center md:hidden">
                    <button
                        onClick={() => {
                            onChange({
                                page: page - 1,
                                perPage,
                            });
                        }}
                        disabled={page === 1 || loading}
                        className={cn(
                            "relative inline-flex items-center rounded-md border bg-card px-4 py-2 text-sm text-slate-600",
                            {
                                "hover:bg-slate-50": page !== 1 && !loading,
                                "cursor-not-allowed opacity-50":
                                    page === 1 && !loading,
                                "cursor-wait opacity-50": loading,
                            },
                        )}
                    >
                        Previous
                    </button>
                    <div>
                        <p className="font-light text-center">
                            Showing <span>{start.toLocaleString()}</span> to{" "}
                            <span>{end.toLocaleString()}</span> of{" "}
                            <span>{resultCount.toLocaleString()}</span> results
                        </p>
                    </div>
                    <button
                        onClick={() => {
                            onChange({
                                page: page + 1,
                                perPage,
                            });
                        }}
                        disabled={page === pageCount || loading}
                        className={cn(
                            "relative ml-3 inline-flex items-center rounded-md border bg-card px-4 py-2 text-sm text-slate-800",
                            {
                                "hover:bg-slate-50":
                                    page !== pageCount && !loading,
                                "cursor-not-allowed opacity-50":
                                    page === pageCount && !loading,
                                "cursor-wait opacity-50": loading,
                            },
                        )}
                    >
                        Next
                    </button>
                </div>
            )}

            <div
                className="hidden md:flex md:flex-1 md:items-center md:justify-between"
                style={{ minHeight: "2.4rem" }}
            >
                <div className="flex items-center gap-2">
                    {enableItemsPerPageSelect && (
                        <ItemsPerPageSelect
                            loading={loading}
                            paginationState={paginationState}
                            onChange={onChange}
                        />
                    )}
                    <p className="font-light whitespace-nowrap">
                        Showing <span>{start.toLocaleString()}</span> to{" "}
                        <span>{end.toLocaleString()}</span> of{" "}
                        <span>{resultCount.toLocaleString()}</span> results
                    </p>
                    {loading && <LoadingSpinner size={4} />}
                </div>

                {disableControls !== true && (
                    <nav
                        className="isolate inline-flex -space-x-px rounded-md shadow-xs"
                        aria-label="Pagination"
                    >
                        <button
                            onClick={() => {
                                onChange({
                                    page: page - 1,
                                    perPage,
                                });
                            }}
                            disabled={page === 1 || loading}
                            className={cn(
                                "w-12 relative inline-flex items-center rounded-l-md border bg-card px-4 py-2 text-sm text-slate-500 focus:z-20",
                                {
                                    "hover:bg-slate-50 z-150":
                                        page !== 1 && !loading,
                                    "cursor-not-allowed opacity-50":
                                        page === 1 && !loading,
                                    "cursor-wait": loading,
                                },
                            )}
                        >
                            <span className="sr-only">Previous</span>
                            <FontAwesomeIcon
                                icon={faChevronLeft}
                                className="h-3 w-3"
                                aria-hidden="true"
                            />
                        </button>

                        {shown.map((pg, index) => {
                            if (pg === ELLIPSIS_SPACER) {
                                return (
                                    <span
                                        key={`page-${pg}-${index}`}
                                        className="w-12 tracking-widest justify-center relative inline-flex items-center border bg-card px-4 py-2 text-xs text-slate-600 cursor-default"
                                    >
                                        ...
                                    </span>
                                );
                            }

                            return (
                                <button
                                    key={`page-${pg}-${index}`}
                                    onClick={() => {
                                        onChange({
                                            page: pg,
                                            perPage,
                                        });
                                    }}
                                    disabled={pg === page || loading}
                                    className={cn(
                                        "w-12 justify-center relative tabular-nums inline-flex items-center border px-4 py-2 text-xs focus:z-20 transition-colors",
                                        {
                                            "border-blue-500 bg-blue-50 text-blue-600 z-50":
                                                pg === page,
                                            "bg-card text-slate-500 z-0":
                                                pg !== page,
                                            "hover:bg-slate-50":
                                                pg !== page && !loading,
                                            "cursor-wait": loading,
                                        },
                                    )}
                                >
                                    {pg}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => {
                                onChange({
                                    page: page + 1,
                                    perPage,
                                });
                            }}
                            disabled={page === pageCount || loading}
                            className={cn(
                                "w-12 relative inline-flex items-center rounded-r-md border bg-card px-4 py-2 text-sm text-slate-600",
                                {
                                    "hover:bg-slate-50":
                                        page !== pageCount && !loading,
                                    "cursor-not-allowed opacity-50":
                                        page === pageCount && !loading,
                                    "cursor-wait": loading,
                                },
                            )}
                        >
                            <span className="sr-only">Next</span>
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className="h-3 w-3"
                                aria-hidden="true"
                            />
                        </button>
                    </nav>
                )}
            </div>
        </div>
    );
}
